import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Shield, Building2, BadgeCheck, CreditCard, FileCheck, ScrollText, Megaphone, Ban, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../api/client';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

export default function Admin() {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [overview, setOverview] = useState(null);
  const [claims, setClaims] = useState([]);
  const [firms, setFirms] = useState([]);
  const [subs, setSubs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState('');

  const loadAll = useCallback(() => {
    adminApi.overview().then(setOverview).catch(() => {});
    adminApi.claims().then(setClaims).catch(() => {});
    adminApi.firms().then(setFirms).catch(() => {});
    adminApi.subscriptions().then(setSubs).catch(() => {});
    adminApi.consents().then(setConsents).catch(() => {});
    adminApi.events().then(setEvents).catch(() => {});
  }, []);

  useEffect(() => { if (user?.role === 'admin') loadAll(); }, [user, loadAll]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const decideClaim = async (id, approve) => {
    if (approve) await adminApi.approveClaim(id); else await adminApi.rejectClaim(id);
    toast({ title: approve ? 'Reivindicação aprovada' : 'Reivindicação rejeitada' });
    loadAll();
  };

  const toggleSuspend = async (firm) => {
    const res = await adminApi.suspend(firm.id);
    toast({ title: res.claimStatus === 'suspenso' ? 'Escritório suspenso' : 'Suspensão removida' });
    loadAll();
  };

  const toggleSponsor = async (firm, sponsored) => {
    await adminApi.sponsor(firm.id, sponsored);
    toast({ title: sponsored ? 'Marcado como patrocinado' : 'Patrocínio removido' });
    loadAll();
  };

  const searchFirms = () => adminApi.firms(q).then(setFirms);

  const claimBadge = { pendente: 'bg-amber-100 text-amber-800', aprovado: 'bg-emerald-100 text-emerald-800', rejeitado: 'bg-red-100 text-red-700' };

  return (
    <div className="aj-wrap py-8" data-testid="admin-panel">
      <SEO title="Painel administrativo" description="Administração da plataforma." />
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-[7px] bg-[#153e44] flex items-center justify-center"><Shield className="w-5 h-5 text-[#075a6d]" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-[#153e44]">Painel administrativo</h1>
          <p className="text-sm text-[#52676e]">Moderação, claims, assinaturas, patrocínio e auditoria</p>
        </div>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {[
            { label: 'Escritórios', value: overview.firms.total, icon: Building2 },
            { label: 'Reivindicados', value: overview.firms.claimed + overview.firms.verified, icon: BadgeCheck },
            { label: 'Claims pendentes', value: overview.pendingClaims, icon: FileCheck },
            { label: 'MRR (simulado)', value: `R$${overview.subscriptions.mrr.toLocaleString('pt-BR')}`, icon: CreditCard },
            { label: 'Consentimentos', value: overview.consents, icon: ScrollText },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-[#cbd8dc] rounded-[7px] p-4">
              <div className="flex items-center gap-2 text-xs text-[#52676e]"><s.icon className="w-3.5 h-3.5 text-[#075a6d]" /> {s.label}</div>
              <div className="text-xl font-semibold text-[#153e44] mt-1" data-testid={`admin-stat-${i}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="claims">
        <TabsList className="bg-white border border-[#cbd8dc] p-1 flex-wrap h-auto">
          <TabsTrigger value="claims" data-testid="admin-tab-claims">Claims</TabsTrigger>
          <TabsTrigger value="escritorios" data-testid="admin-tab-firms">Escritórios</TabsTrigger>
          <TabsTrigger value="assinaturas" data-testid="admin-tab-subs">Assinaturas</TabsTrigger>
          <TabsTrigger value="consentimentos" data-testid="admin-tab-consents">Consentimentos</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="admin-tab-audit">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="claims" className="mt-4 space-y-2">
          {claims.length === 0 && <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-8 text-center text-[#52676e] text-sm">Nenhuma reivindicação registrada.</div>}
          {claims.map(c => (
            <div key={c.id} className="bg-white border border-[#cbd8dc] rounded-[7px] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-claim-${c.id}`}>
              <div>
                <div className="font-semibold text-[#153e44] text-sm">{c.firmName}</div>
                <div className="text-xs text-[#52676e]">{c.userEmail} • {c.position} {c.oab && `• ${c.oab}`} • Termos {c.termsVersion}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${claimBadge[c.status] || 'bg-[#f2f6f7]'}`}>{c.status}</span>
                {c.status === 'pendente' && (
                  <>
                    <Button size="sm" onClick={() => decideClaim(c.id, true)} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`admin-approve-${c.id}`}>Aprovar</Button>
                    <Button size="sm" variant="outline" onClick={() => decideClaim(c.id, false)} className="text-red-600 border-red-200 h-8" data-testid={`admin-reject-${c.id}`}>Rejeitar</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="escritorios" className="mt-4 space-y-3">
          <div className="flex gap-2">
            <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') searchFirms(); }} placeholder="Buscar por nome..." className="max-w-xs" data-testid="admin-firms-search-input" />
            <Button variant="outline" onClick={searchFirms} data-testid="admin-firms-search-btn"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {firms.map(f => (
              <div key={f.id} className="bg-white border border-[#cbd8dc] rounded-[7px] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-firm-${f.id}`}>
                <div>
                  <div className="font-semibold text-[#153e44] text-sm">{f.name}</div>
                  <div className="text-xs text-[#52676e]">{f.cityName}/{f.state} • Plano: {f.plan} • Status: {f.claimStatus}</div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs text-[#425a62]">
                    <Megaphone className="w-3.5 h-3.5 text-[#075a6d]" /> Patrocinado
                    <Switch checked={!!f.sponsored} onCheckedChange={v => toggleSponsor(f, v)} data-testid={`admin-sponsor-switch-${f.id}`} />
                  </label>
                  <Button size="sm" variant={f.claimStatus === 'suspenso' ? 'default' : 'outline'} onClick={() => toggleSuspend(f)} className={f.claimStatus === 'suspenso' ? 'bg-emerald-600 hover:bg-emerald-700 h-8' : 'text-red-600 border-red-200 h-8'} data-testid={`admin-suspend-${f.id}`}>
                    <Ban className="w-3.5 h-3.5 mr-1" /> {f.claimStatus === 'suspenso' ? 'Reativar' : 'Suspender'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assinaturas" className="mt-4 space-y-2">
          {subs.length === 0 && <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-8 text-center text-[#52676e] text-sm">Nenhuma assinatura registrada.</div>}
          {subs.map(s => (
            <div key={s.id} className="bg-white border border-[#cbd8dc] rounded-[7px] p-4 flex flex-wrap items-center justify-between gap-2 text-sm">
              <div>
                <span className="font-semibold text-[#153e44]">{s.firmName}</span>
                <span className="text-[#52676e]"> • {s.planId} • R${(s.price || 0).toLocaleString('pt-BR')}/mês</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-[#6b858e]">{s.createdAt?.slice(0, 10)}</span>
                <span className={`font-semibold px-2.5 py-1 rounded-full ${s.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-[#f2f6f7] text-[#52676e]'}`}>{s.status}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="consentimentos" className="mt-4 space-y-2">
          {consents.length === 0 && <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-8 text-center text-[#52676e] text-sm">Nenhum consentimento registrado.</div>}
          {consents.map(c => (
            <div key={c.id} className="bg-white border border-[#cbd8dc] rounded-[7px] p-4 text-sm">
              <div className="font-semibold text-[#153e44]">{c.subjectName || 'Titular'} → {c.recipient}</div>
              <div className="text-xs text-[#52676e] mt-0.5">{c.purpose} • Versão {c.version} • {new Date(c.grantedAt).toLocaleString('pt-BR')}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="auditoria" className="mt-4 space-y-1.5">
          {events.length === 0 && <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-8 text-center text-[#52676e] text-sm">Nenhum evento registrado.</div>}
          {events.map(e => (
            <div key={e.id} className="bg-white border border-[#cbd8dc] rounded-[7px] px-4 py-2.5 text-xs flex flex-wrap items-center gap-2">
              <span className="font-mono font-semibold text-[#153e44]">{e.eventName}</span>
              <span className="text-[#52676e]">{e.entityType}:{String(e.entityId).slice(0, 8)}</span>
              <span className="text-[#6b858e] ml-auto">{new Date(e.occurredAt).toLocaleString('pt-BR')}</span>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
